/**
 * uploadObjectKey — build the S3 object key for ONE upload, collision-safe.
 *
 * WHY THIS FILE EXISTS (measured defect, 2026-07-31)
 * `s3Helper.uploadFile(file, folderStructure)` used to derive its key as
 * `${folderStructure}${slug(file.name)}` — a pure function of the FILENAME with
 * no uniqueness component. Two uploads of the same filename into the same
 * folder therefore resolved to the SAME key, and the second presigned PUT
 * silently OVERWROTE the first object. Kelly's QA-52 recording shows the user-
 * visible result at f032: three separate "Photo" captures, one identical
 * thumbnail.
 *
 * The capture path was fixed first, at its own call site. That left the SHARED
 * primitive still collision-prone for every other upload surface (wheel media,
 * AI builder, Business Box, `utils/fileUpload`). Per G-118 a security/
 * correctness fix applied to a shared util's CALLERS is only as complete as the
 * grep that found them — so the guarantee now lives in the primitive, and this
 * module is the single place that decides what an upload key looks like.
 *
 * WHAT IT GUARANTEES
 *  - two calls with the same folder + filename return DIFFERENT keys;
 *  - the folder prefix is preserved byte-for-byte (backends classify by prefix —
 *    e.g. `users/views.py::annotate_media_with_capture_source` decides an item is
 *    a capture with `startswith("uploads/<id>/capture/")`), so uniqueness goes
 *    into the FILENAME and never adds a path segment;
 *  - the user's original filename stays readable in the key.
 */

/** S3-safe slug. Kept identical to the rules s3Helper has always applied. */
export const slugifyFilename = (filename) =>
  String(filename)
    .replace(/[^a-zA-Z0-9.]/g, "-")
    .replace(/-+/g, "-")
    .toLowerCase();

/**
 * A short, collision-resistant token for one upload.
 * `crypto.randomUUID` when available, else `Math.random` — both are ample: the
 * token only separates concurrent uploads into one prefix, it is not a secret.
 *
 * `window` rather than `globalThis`: this project's ESLint parser options predate
 * the es2020 global set, so `globalThis` is an undeclared identifier here, and
 * `self` is banned by `no-restricted-globals`. Every runtime this module actually
 * executes in (the browser and jsdom) provides `window`; the `{}` fallback keeps
 * the `crypto` probe total under any other host, which then takes the Math.random
 * branch below — asserted by uploadObjectKey.test.js.
 */
const globalScope = typeof window !== "undefined" ? window : {};

export function uploadToken() {
  const uuid =
    globalScope.crypto && typeof globalScope.crypto.randomUUID === "function"
      ? globalScope.crypto.randomUUID()
      : null;
  const rand = uuid
    ? uuid.replace(/-/g, "").slice(0, 8)
    : Math.random().toString(36).slice(2, 10).padEnd(8, "0");
  return `${Date.now().toString(36)}-${rand}`;
}

/**
 * @param {string} folderStructure e.g. `uploads/42/wheels/2806/` (trailing slash)
 * @param {string} filename        the user's original file name
 * @param {string} [token]         injectable uniqueness token (tests pin it)
 * @returns {string} e.g. `uploads/42/wheels/2806/m9x1k2-1a2b3c4d-dog.gif`
 */
export function buildUniqueObjectKey(
  folderStructure,
  filename,
  token = uploadToken()
) {
  return `${folderStructure || ""}${token}-${slugifyFilename(filename)}`;
}
